/**
 * @fileoverview Typography Section - Component Library
 *
 * Purpose: Display all typography styles and text variants from the design system
 * Used by: Component library main page
 */

export function TypographySection() {
  return (
    <div id="typography" className="scroll-mt-24 space-y-12">
      <div>
        <h1 className="mb-2 text-4xl font-bold tracking-tight">Typography</h1>
        <p className="text-lg text-muted-foreground">
          Our design system's typography scale, weights, and text styles. Edit
          in{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            tailwind.config.ts
          </code>{" "}
          and{" "}
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
            src/index.css
          </code>
          .
        </p>
      </div>

      {/* Headings */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Headings</h2>
          <p className="text-muted-foreground">
            Heading styles from H1 to H6 with recommended use cases.
          </p>
        </div>
        <div className="space-y-6 rounded-lg border bg-card p-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-extrabold tracking-tight">
              Heading 1 - Extra Bold
            </h1>
            <p className="font-mono text-sm text-muted-foreground">
              text-4xl font-extrabold tracking-tight
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Page titles, hero sections
            </p>
          </div>

          <div className="space-y-2">
            <h2 className="text-3xl font-bold tracking-tight">
              Heading 2 - Bold
            </h2>
            <p className="font-mono text-sm text-muted-foreground">
              text-3xl font-bold tracking-tight
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Section titles, main headings
            </p>
          </div>

          <div className="space-y-2">
            <h3 className="text-2xl font-semibold tracking-tight">
              Heading 3 - Semibold
            </h3>
            <p className="font-mono text-sm text-muted-foreground">
              text-2xl font-semibold tracking-tight
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Subsection titles, card headers
            </p>
          </div>

          <div className="space-y-2">
            <h4 className="text-xl font-semibold tracking-tight">
              Heading 4 - Semibold
            </h4>
            <p className="font-mono text-sm text-muted-foreground">
              text-xl font-semibold tracking-tight
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Component titles, list headers
            </p>
          </div>

          <div className="space-y-2">
            <h5 className="text-lg font-medium">Heading 5 - Medium</h5>
            <p className="font-mono text-sm text-muted-foreground">
              text-lg font-medium
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Small section titles
            </p>
          </div>

          <div className="space-y-2">
            <h6 className="text-base font-medium">Heading 6 - Medium</h6>
            <p className="font-mono text-sm text-muted-foreground">
              text-base font-medium
            </p>
            <p className="text-xs text-muted-foreground">
              Use for: Inline headings, labels
            </p>
          </div>
        </div>
      </section>

      {/* Body Text */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Body Text</h2>
          <p className="text-muted-foreground">
            Paragraph and body text styles with different sizes.
          </p>
        </div>
        <div className="space-y-6 rounded-lg border bg-card p-8">
          <div className="space-y-2">
            <p className="text-lg">
              Large body text - This is larger body text used for lead
              paragraphs or important content that needs emphasis without being
              a heading.
            </p>
            <p className="font-mono text-sm text-muted-foreground">text-lg</p>
          </div>

          <div className="space-y-2">
            <p className="text-base">
              Base body text - This is the default body text size. Used for most
              content, descriptions, and general text throughout the
              application.
            </p>
            <p className="font-mono text-sm text-muted-foreground">
              text-base (default)
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm">
              Small body text - Used for secondary information, captions, helper
              text, and less important content.
            </p>
            <p className="font-mono text-sm text-muted-foreground">text-sm</p>
          </div>

          <div className="space-y-2">
            <p className="text-xs">
              Extra small text - Used for labels, metadata, timestamps, and very
              subtle information.
            </p>
            <p className="font-mono text-sm text-muted-foreground">text-xs</p>
          </div>
        </div>
      </section>

      {/* Font Weights */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Font Weights
          </h2>
          <p className="text-muted-foreground">
            Available font weight variants for emphasis and hierarchy.
          </p>
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-8">
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-light">Light</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-light
            </code>
          </div>
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-normal">Normal</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-normal
            </code>
          </div>
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-medium">Medium</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-medium
            </code>
          </div>
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-semibold">Semibold</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-semibold
            </code>
          </div>
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-bold">Bold</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-bold
            </code>
          </div>
          <div className="flex items-baseline gap-4">
            <span className="w-32 text-lg font-extrabold">Extra Bold</span>
            <code className="font-mono text-sm text-muted-foreground">
              font-extrabold
            </code>
          </div>
        </div>
      </section>

      {/* Text Colors */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Text Colors
          </h2>
          <p className="text-muted-foreground">
            Semantic text colors that adapt to light/dark themes.
          </p>
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-8">
          <div className="space-y-2">
            <p className="text-foreground">Default foreground text</p>
            <code className="font-mono text-sm text-muted-foreground">
              text-foreground
            </code>
          </div>
          <div className="space-y-2">
            <p className="text-muted-foreground">
              Muted foreground text for secondary content
            </p>
            <code className="font-mono text-sm text-muted-foreground">
              text-muted-foreground
            </code>
          </div>
          <div className="space-y-2">
            <p className="text-primary">
              Primary colored text for links and emphasis
            </p>
            <code className="font-mono text-sm text-muted-foreground">
              text-primary
            </code>
          </div>
          <div className="space-y-2">
            <p className="text-destructive">
              Destructive text for errors and warnings
            </p>
            <code className="font-mono text-sm text-muted-foreground">
              text-destructive
            </code>
          </div>
        </div>
      </section>

      {/* Lists */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Lists</h2>
          <p className="text-muted-foreground">
            Styled ordered and unordered lists.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-3 rounded-lg border bg-card p-8">
            <h3 className="text-lg font-semibold">Unordered List</h3>
            <ul className="list-inside list-disc space-y-2">
              <li>First list item</li>
              <li>Second list item</li>
              <li>Third list item with more content to show wrapping</li>
            </ul>
            <code className="font-mono text-sm text-muted-foreground">
              list-disc list-inside
            </code>
          </div>

          <div className="space-y-3 rounded-lg border bg-card p-8">
            <h3 className="text-lg font-semibold">Ordered List</h3>
            <ol className="list-inside list-decimal space-y-2">
              <li>First step</li>
              <li>Second step</li>
              <li>Third step with more detailed instructions</li>
            </ol>
            <code className="font-mono text-sm text-muted-foreground">
              list-decimal list-inside
            </code>
          </div>
        </div>
      </section>

      {/* Inline Styles */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Inline Styles
          </h2>
          <p className="text-muted-foreground">
            Inline text formatting and emphasis.
          </p>
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-8">
          <p>
            This is <strong className="font-semibold">bold text</strong> using
            strong tag.
          </p>
          <p>
            This is <em className="italic">italic text</em> using em tag.
          </p>
          <p>
            This is <span className="underline">underlined text</span> using
            underline class.
          </p>
          <p>
            This is <span className="line-through">strikethrough text</span>{" "}
            using line-through class.
          </p>
          <p>
            This is{" "}
            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm">
              inline code
            </code>{" "}
            using code tag.
          </p>
          <p>
            This is a{" "}
            <a
              href="#"
              className="text-primary underline underline-offset-4 hover:text-primary/80"
            >
              text link
            </a>{" "}
            with underline.
          </p>
        </div>
      </section>

      {/* Code Blocks */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Code</h2>
          <p className="text-muted-foreground">
            Code display styles for inline and block code.
          </p>
        </div>
        <div className="space-y-6 rounded-lg border bg-card p-8">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Inline Code</h3>
            <p>
              Use the{" "}
              <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold">
                npm install
              </code>{" "}
              command to install dependencies.
            </p>
            <code className="font-mono text-xs text-muted-foreground">
              className="relative rounded bg-muted px-[0.3rem] py-[0.2rem]
              font-mono text-sm"
            </code>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Code Block</h3>
            <pre className="overflow-x-auto rounded-lg bg-muted p-4">
              <code className="font-mono text-sm">
                {`function hello() {
  console.log("Hello, world!");
  return true;
}`}
              </code>
            </pre>
            <code className="font-mono text-xs text-muted-foreground">
              pre + code with bg-muted
            </code>
          </div>
        </div>
      </section>

      {/* Blockquote */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">Blockquote</h2>
          <p className="text-muted-foreground">Quotation and callout styles.</p>
        </div>
        <div className="rounded-lg border bg-card p-8">
          <blockquote className="border-l-4 border-primary pl-6 italic text-muted-foreground">
            "Design is not just what it looks like and feels like. Design is how
            it works."
            <footer className="mt-2 text-sm font-normal not-italic">
              — Steve Jobs
            </footer>
          </blockquote>
          <code className="mt-4 block font-mono text-xs text-muted-foreground">
            border-l-4 border-primary pl-6 italic
          </code>
        </div>
      </section>

      {/* Lead Text */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Lead Paragraph
          </h2>
          <p className="text-muted-foreground">
            Larger text for introductions and important content.
          </p>
        </div>
        <div className="space-y-3 rounded-lg border bg-card p-8">
          <p className="text-xl text-muted-foreground">
            This is a lead paragraph with larger text. It's used for article
            introductions, feature descriptions, or any content that deserves
            extra prominence.
          </p>
          <code className="font-mono text-sm text-muted-foreground">
            text-xl text-muted-foreground
          </code>
        </div>
      </section>

      {/* Muted Text */}
      <section className="space-y-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold tracking-tight">
            Muted & Small Text
          </h2>
          <p className="text-muted-foreground">
            Smaller, less prominent text styles.
          </p>
        </div>
        <div className="space-y-4 rounded-lg border bg-card p-8">
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              This is muted text used for descriptions, help text, and secondary
              information.
            </p>
            <code className="font-mono text-xs text-muted-foreground">
              text-sm text-muted-foreground
            </code>
          </div>

          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Extra small muted text for timestamps, metadata, and fine print.
            </p>
            <code className="font-mono text-xs text-muted-foreground">
              text-xs text-muted-foreground
            </code>
          </div>
        </div>
      </section>

      {/* How to Edit */}
      <section className="rounded-lg border bg-card p-8 text-card-foreground">
        <h2 className="mb-4 text-xl font-semibold">How to Edit Typography</h2>
        <div className="space-y-4 text-sm text-muted-foreground">
          <p>
            Typography is controlled by Tailwind CSS utilities and can be
            customized in two places:
          </p>
          <div className="space-y-3">
            <div>
              <p className="mb-2 font-medium text-foreground">
                1. Font Family (src/index.css)
              </p>
              <div className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
                <pre>{`body {
  font-family: system-ui, sans-serif;
}`}</pre>
              </div>
            </div>

            <div>
              <p className="mb-2 font-medium text-foreground">
                2. Font Sizes (tailwind.config.ts)
              </p>
              <div className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs">
                <pre>{`theme: {
  extend: {
    fontSize: {
      xs: ['0.75rem', { lineHeight: '1rem' }],
      sm: ['0.875rem', { lineHeight: '1.25rem' }],
      base: ['1rem', { lineHeight: '1.5rem' }],
      // ... etc
    }
  }
}`}</pre>
              </div>
            </div>

            <div>
              <p className="mb-2 font-medium text-foreground">
                3. Common Text Utilities
              </p>
              <ul className="ml-2 list-inside list-disc space-y-1">
                <li>
                  <code className="rounded bg-muted px-1.5 py-0.5">
                    text-{"{size}"}
                  </code>{" "}
                  - Font size (xs, sm, base, lg, xl, 2xl, 3xl, 4xl)
                </li>
                <li>
                  <code className="rounded bg-muted px-1.5 py-0.5">
                    font-{"{weight}"}
                  </code>{" "}
                  - Weight (light, normal, medium, semibold, bold, extrabold)
                </li>
                <li>
                  <code className="rounded bg-muted px-1.5 py-0.5">
                    tracking-{"{type}"}
                  </code>{" "}
                  - Letter spacing (tighter, tight, normal, wide, wider)
                </li>
                <li>
                  <code className="rounded bg-muted px-1.5 py-0.5">
                    leading-{"{type}"}
                  </code>{" "}
                  - Line height (none, tight, snug, normal, relaxed, loose)
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
